import { useEffect, useRef, useState } from 'react';
import type { IconType } from 'react-icons';
import { FiChevronDown, FiLogOut } from 'react-icons/fi';
import { MdAdminPanelSettings, MdApartment, MdDirectionsBike, MdSettingsBrightness } from 'react-icons/md';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { useTema } from '@/contexts/TemaContext';
import { appVersion } from '@/config/aws';

const ROLE_META: Record<'Admins' | 'Clubes' | 'Ciclistas', { label: string; icon: IconType }> = {
  Admins: { label: 'Administrador', icon: MdAdminPanelSettings },
  Clubes: { label: 'Clube', icon: MdApartment },
  Ciclistas: { label: 'Randonneur', icon: MdDirectionsBike },
};

function getInitials(name?: string, email?: string): string {
  const source = name?.trim() || email?.split('@')[0] || '';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const ultimo = parts.at(-1)?.[0] ?? '';
  return (parts[0][0] + ultimo).toUpperCase();
}

export default function UserMenu() {
  const { user, logout, isAdmin, isClubes, isCiclista } = useAuth();
  const { temEscolhaExplicita, seguirSistema } = useTema();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  if (!user) return null;

  let roleKey: keyof typeof ROLE_META | undefined;
  if (isAdmin()) roleKey = 'Admins';
  else if (isClubes()) roleKey = 'Clubes';
  else if (isCiclista()) roleKey = 'Ciclistas';
  const role = roleKey ? ROLE_META[roleKey] : undefined;
  const RoleIcon = role?.icon;
  const clubeLabel = isClubes() && user.idClube ? user.nomeClube || `Clube #${user.idClube}` : undefined;

  const initials = getInitials(user.name, user.email);
  const displayName = user.name || user.email;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu do usuário"
        className="flex items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-2 text-ink-body transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <span
          aria-hidden="true"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-ink-on-brand"
        >
          {initials}
        </span>
        <span className="hidden max-w-[10rem] truncate text-sm font-medium text-ink sm:block">
          {displayName}
        </span>
        <FiChevronDown
          size={16}
          className={clsx('text-ink-faint transition-transform motion-reduce:transition-none', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Menu do usuário"
          className="absolute right-0 z-20 mt-2 w-72 origin-top-right rounded-lg border border-line bg-surface shadow-lg animate-menuIn motion-reduce:animate-none"
        >
          <div className="px-4 py-3">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-ink-on-brand"
              >
                {initials}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{displayName}</p>
                <p className="truncate text-xs text-ink-soft">{user.email}</p>
              </div>
            </div>

            {(role ?? clubeLabel) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {role && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
                    {RoleIcon && <RoleIcon size={13} />}
                    {role.label}
                  </span>
                )}
                {clubeLabel && (
                  <span className="inline-flex items-center rounded-full bg-surface-sunken px-2.5 py-1 text-xs font-medium text-ink-muted">
                    {clubeLabel}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Só aparece quando há escolha explícita gravada: sem ela o estado
              já é o do sistema, e a entrada ofereceria algo que não faz nada.
              É a única saída de volta ao padrão — o switch da barra é binário,
              e "seguir o sistema" some no primeiro acionamento dele. */}
          {temEscolhaExplicita && (
            <div className="border-t border-line py-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  seguirSistema();
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-hover"
              >
                <MdSettingsBrightness size={16} />
                Seguir o tema do sistema
              </button>
            </div>
          )}

          <div className="border-t border-line-soft py-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-danger transition-colors hover:bg-danger-tint"
            >
              <FiLogOut size={16} />
              Sair
            </button>
          </div>

          <div className="border-t border-line-soft px-4 py-2">
            <p className="text-center text-xs text-ink-faint">{appVersion}</p>
          </div>
        </div>
      )}
    </div>
  );
}
